import { useCallback, useEffect, useState } from 'react';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import { SectionCard } from '../../components/PageChrome';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { slugify } from '../../utils/format';

const emptyCategory = {
  name: '',
  slug: '',
  description: '',
  parent_id: '',
};

export default function CategoriesPage() {
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyCategory);
  const [saving, setSaving] = useState(false);

  const loadCategories = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await shopApi.listCategories(token, selectedShopId);
      setCategories(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleNameChange = (name) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: prev.slug || slugify(name),
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.createCategory(token, selectedShopId, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
      });
      setModalOpen(false);
      setForm(emptyCategory);
      await loadCategories();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Categories"
        description="Organize your catalog with hierarchical categories."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Add category
          </button>
        }
      />

      <ErrorBanner message={error} />

      {loading ? (
        <LoadingState />
      ) : categories.length === 0 ? (
        <EmptyState message="No categories yet. Create one before adding products." />
      ) : (
        <SectionCard title="All categories">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Handle</th>
                <th>Parent</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td>{category.name}</td>
                  <td>
                    <code>{category.slug}</code>
                  </td>
                  <td>{category.parent_id ? `#${category.parent_id}` : '—'}</td>
                  <td>{category.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      <Modal
        title="Add category"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="category-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create category'}
            </button>
          </>
        }
      >
        <form id="category-form" className="form-grid" onSubmit={handleCreate}>
          <div className="form-field">
            <label htmlFor="cat-name">Name</label>
            <input
              id="cat-name"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor="cat-slug">URL handle</label>
            <input
              id="cat-slug"
              value={form.slug}
              onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor="cat-parent">Parent category</label>
            <select
              id="cat-parent"
              value={form.parent_id}
              onChange={(e) => setForm((p) => ({ ...p, parent_id: e.target.value }))}
            >
              <option value="">None (top level)</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="cat-desc">Description</label>
            <textarea
              id="cat-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
        </form>
      </Modal>
    </RequireShop>
  );
}
